import { writeShortUrlParams, getShortUrlParams } from '@intelligence/services/common';
import { history, History } from 'umi';
import { feachTimeTypeToFilterTimeType } from '@/pages/intelligence/utils/common';
import moment from 'moment';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { onFilterConfirm } from '@intelligence/components/Filter/util';

// 短链接名
export const URL_SHORT_LINK_NAME = 'filters_key';
// 写到接口的筛选器key
export const URL_FILTER_KEY = 'filter';
// 可视化可点击的指标值
export const URL_CHART_METRIC = 'chart_metric';
// 柱饼折类型
export const URL_CHART_TYPE = 'chart_type';
// 表格排序的指标值
export const URL_SORT_NAME = 'sort_name';
// 表格升序降序
export const URL_ORDER = 'order';
// 表格指标值对应的数据源，辅助作用
export const URL_SOURCE_NAME = 'source';
// 表格分页
export const URL_PAGE_SIZE = 'pageSize';
// 表格排序按值0按环比1
export const URL_RATIO_TYPE = 'ratio_type';
// 表格上面标签滚动表格用的
export const URL_TABLE_SCROLL_SEG = 'table_segement';
// 如详情页一上页面有2个卡片需要配置，副本一样的存在
export const URL_2_ORDER = `${URL_ORDER}_2`;
export const URL_2_RATIO_TYPE = `${URL_RATIO_TYPE}_2`;
export const URL_2_SORT_NAME = `${URL_SORT_NAME}_2`;
export const URL_2_CHART_TYPE = `${URL_CHART_TYPE}_2`;
export const URL_2_CHART_METRIC = `${URL_CHART_METRIC}_2`;
export const URL_COUNTRIES_TYPE = 'countries_type';
// mobile详情头部的下拉选项
export const URL_APP_IDS = 'app_id';
// mobile详情头部的下拉选项选择为1个时展示的stats页面多一个rank开关
export const URL_RANK_SWITCH = 'rank_switch';

/**
 * @URL_SHORT_LINK_NAME - 短链接名
 * @URL_CHART_METRIC - 可视化可点击的指标值
 */
export type ShortLinkQueryType = {
  // 短链接名
  [URL_SHORT_LINK_NAME]?: string;
  // 可视化可点击的指标值
  [URL_CHART_METRIC]?: string;
  // 柱饼折类型
  [URL_CHART_TYPE]?: string;
  // 表格排序的指标值
  [URL_SORT_NAME]?: string;
  // 表格升序降序
  [URL_ORDER]?: string;
  // 表格指标值对应的数据源，辅助作用
  [URL_SOURCE_NAME]?: string;
  // 表格分页
  [URL_PAGE_SIZE]?: string | number;
  // 表格排序按值0按环比1
  [URL_RATIO_TYPE]?: string | number;
  // 表格上面标签滚动表格用的
  [URL_TABLE_SCROLL_SEG]?: string;
  [URL_2_ORDER]?: string;
  [URL_2_RATIO_TYPE]?: string | number;
  [URL_2_SORT_NAME]?: string;
  [URL_2_CHART_TYPE]?: string;
  [URL_2_CHART_METRIC]?: string;
  [URL_COUNTRIES_TYPE]?: string;
  [URL_APP_IDS]?: string;
  [URL_RANK_SWITCH]?: boolean;
};

export type ShortLinkPageValidCfgType = {
  [URL_CHART_METRIC]?: string[];
  [URL_CHART_TYPE]?: string[];
  [URL_SORT_NAME]?: string[];
  [URL_ORDER]?: string[];
  [URL_SOURCE_NAME]?: string[];
  [URL_PAGE_SIZE]?: string[];
  [URL_RATIO_TYPE]?: string[];
  [URL_TABLE_SCROLL_SEG]?: string[];
  [URL_2_ORDER]?: string[];
  [URL_2_RATIO_TYPE]?: string[];
  [URL_2_SORT_NAME]?: string[];
  [URL_2_CHART_TYPE]?: string[];
  [URL_2_CHART_METRIC]?: string[];
  [URL_COUNTRIES_TYPE]?: string[];
  [URL_RANK_SWITCH]?: boolean[];
};

// 在更新短链接url参数时，需要删除的字段
// const urlParamsBlackList = ['filter_tag_name'];
const urlParamsBlackList: string[] = [];
function jsonToUrlParams(json: { [key: string]: string | number | boolean }) {
  if (!json || typeof json !== 'object') {
    return '';
  }
  return Object.keys(json)
    .filter((key) => !urlParamsBlackList.includes(key))
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(json[key])}`)
    .join('&');
}

// 操作筛选器，更新url
const writeShortLinkFilter = async (params: { [URL_FILTER_KEY]: any; [key: string]: any }) => {
  try {
    const res = await writeShortUrlParams({
      system_type: 'intelligence',
      params: JSON.stringify(params),
    });
    return res?.data?.key ?? '';
  } catch (err) {
    return '';
  }
};

// 获取短链接上的URL_SHORT_LINK_NAME得到对应的接口值
export const getOriginalShortLinkFilter = async () => {
  const id = history?.location?.query?.[URL_SHORT_LINK_NAME];
  if (!id || typeof id !== 'string') {
    return {};
  }
  try {
    const res = await getShortUrlParams({ key: id });
    const data = JSON.parse(res?.data?.params ?? '{}');
    return { ...data, filter: { ...data?.filter, steam_tag: { value: [] } } };
  } catch (err) {
    return {};
  }
};

export const updateShortLinkFilterId = async (params: { [URL_FILTER_KEY]: any; [key: string]: any }) => {
  const shortLinkId = await writeShortLinkFilter(params);
  if (shortLinkId) {
    updateShortLinkPath({ shortLinkId });
  }
};

// 更新短链接路径
export const updateShortLinkPath = (params: { shortLinkId?: string; shortLinkQuery?: ShortLinkQueryType }) => {
  const { shortLinkId, shortLinkQuery = {} } = params || {};
  const url = new URL(window.location.href);
  const query = new URLSearchParams(url.search);
  let queryObj: { [key: string]: string | number | boolean } = {};
  query.forEach((value, key) => {
    queryObj[key] = value;
  });
  queryObj = { ...queryObj, ...shortLinkQuery };

  const { [URL_SHORT_LINK_NAME]: oldShortLinkId, ...newQuery } = queryObj;
  // 短链接id有新的就新的，没有用旧的，否则不用
  const shortLink = shortLinkId || oldShortLinkId;
  const queryParams = shortLink
    ? `${URL_SHORT_LINK_NAME}=${shortLink}&${jsonToUrlParams(newQuery)}`
    : jsonToUrlParams(newQuery);
  const newPath = `?${queryParams}`;
  history.replace(newPath);
};

// 校正url输入的是不是页面可读取的，不是返回默认值
export const checkValidQuery = (history: History, validCfg: ShortLinkPageValidCfgType): ShortLinkQueryType => {
  const { query } = history?.location || {};
  function getValidValue(validCfg: any, query: any, urlKey: string) {
    const queryKeyValue = Array.isArray(query?.[urlKey]) ? query[urlKey][query[urlKey].length - 1] : query?.[urlKey];
    const queryValue = typeof queryKeyValue === 'string' ? queryKeyValue.toLocaleLowerCase() : undefined;
    const validValues = validCfg?.[urlKey]?.map((t: string | boolean) => {
      if (typeof t === 'string') return t?.toLocaleLowerCase();
      return String(t);
    });
    const queryValueIndex = validValues?.indexOf(queryValue);
    // 如果小写一至，用我们定义好的指标大小写，因为要作为参数所以严格返回大小写
    return queryValueIndex > -1 ? validCfg?.[urlKey]?.[queryValueIndex] : validCfg?.[urlKey]?.[0];
  }
  return {
    [URL_SORT_NAME]: getValidValue(validCfg, query, URL_SORT_NAME),
    [URL_PAGE_SIZE]: Number(getValidValue(validCfg, query, URL_PAGE_SIZE)),
    [URL_ORDER]: getValidValue(validCfg, query, URL_ORDER),
    [URL_CHART_TYPE]: getValidValue(validCfg, query, URL_CHART_TYPE),
    [URL_RATIO_TYPE]: Number(getValidValue(validCfg, query, URL_RATIO_TYPE)),
    [URL_CHART_METRIC]: getValidValue(validCfg, query, URL_CHART_METRIC),
    [URL_SOURCE_NAME]: getValidValue(validCfg, query, URL_SOURCE_NAME),
    [URL_TABLE_SCROLL_SEG]: getValidValue(validCfg, query, URL_TABLE_SCROLL_SEG),
    [URL_2_ORDER]: getValidValue(validCfg, query, URL_2_ORDER),
    [URL_2_RATIO_TYPE]: Number(getValidValue(validCfg, query, URL_2_RATIO_TYPE)),
    [URL_2_SORT_NAME]: getValidValue(validCfg, query, URL_2_SORT_NAME),
    [URL_2_CHART_TYPE]: getValidValue(validCfg, query, URL_2_CHART_TYPE),
    [URL_2_CHART_METRIC]: getValidValue(validCfg, query, URL_2_CHART_METRIC),
    [URL_COUNTRIES_TYPE]: getValidValue(validCfg, query, URL_COUNTRIES_TYPE),
    [URL_RANK_SWITCH]: Boolean(getValidValue(validCfg, query, URL_RANK_SWITCH)),
  };
};

// 更新时间筛选器
export const updateTimeFilterValue = (params: {
  haveFilterSaveBtn?: boolean;
  filterSaveName?: string;
  filterValue: any;
  timeFilterValues: any;
}) => {
  // const hasUrlShortLinkVal = !!history?.location?.query?.[URL_SHORT_LINK_NAME];

  const { /* haveFilterSaveBtn, filterSaveName,*/ filterValue, timeFilterValues } = params;
  // 更新时间筛选器
  if (Array.isArray(timeFilterValues?.value) && timeFilterValues?.value.length === 2) {
    const newFormValue = {
      ...filterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: feachTimeTypeToFilterTimeType(timeFilterValues?.type), // TODO
        value: [moment(timeFilterValues?.value[0]), moment(timeFilterValues?.value[1])],
      },
      [filterKeyMap.TIME_TYPE]: undefined,
    };
    // 如果没有短链接，但有保存方案，则展示保存方案名
    // if (!hasUrlShortLinkVal && haveFilterSaveBtn && filterSaveName) {
    //   newFormValue = {
    //     ...newFormValue,
    //     [filterKeyMap.MY_FILTER]: {
    //       value: filterSaveName,
    //     },
    //   };
    // }
    const { apiValue } = onFilterConfirm(newFormValue);
    return apiValue;
  }
  return filterValue;
};
